import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import { formatCurrency } from '../utils/helpers';

const empty = { account_code:'', account_name:'', account_type:'asset', parent_id:'', opening_balance:0, status:1 };

export default function Accounts() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');

  const load = () => api.get('/accounts').then(r => setList(r.data.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.account_name || !form.account_code) return toast.error('Code and Name required');
    try {
      if (editId) await api.put('/accounts/' + editId, form);
      else await api.post('/accounts', form);
      toast.success('Saved'); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const edit = (a) => { setForm({...empty, ...a}); setEditId(a.id); setOpen(true); };

  const types = {asset:'badge-green', liability:'badge-red', equity:'badge-purple', income:'badge-blue', expense:'badge-orange'};

  const filtered = list.filter(a => {
    if (filterStatus === 'active') return Number(a.status) === 1;
    if (filterStatus === 'inactive') return Number(a.status) !== 1;
    return true;
  });

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;
  return (
    <div className="card">
      <div className="card-header">
        <h3>Chart of Accounts ({filtered.length})<PendingCountBadge count={pendingCount}/></h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Account</button>
      </div>
      <div className="search-bar" style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{maxWidth:180}}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">Active + Inactive</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Code</th><th>Account Name</th><th>Type</th><th className="td-num">Opening Balance</th><th className="td-num">Current Balance</th><th>Status</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {filtered.length ? filtered.map(a => (
              <tr key={a.id} style={Number(a.status)!==1?{background:'#f8fafc',color:'#94a3b8'}:a.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                <td><strong>{a.account_code}</strong></td>
                <td>{a.account_name}</td>
                <td><span className={'badge ' + types[a.account_type]}>{a.account_type}</span></td>
                <td className="td-num">{formatCurrency(a.opening_balance)}</td>
                <td className="td-num">{formatCurrency(a.current_balance)}</td>
                <td><span className={'badge ' + (a.status==1?'badge-green':'badge-red')}>{a.status==1?'Active':'Inactive'}</span></td>
                <td><ApprovalBadge status={a.approval_status} createdByName={a.created_by_name}/></td>
                <td>
                  <RowActions endpoint="/accounts" row={a} onChange={load} onEdit={edit} softDelete restoreAction="restore" confirmDeleteMsg="Delete this account?"/>
                </td>
              </tr>
            )) : <tr><td colSpan="8" className="empty">No accounts</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Account':'Add Account'}
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId?'Update':'Create'}</button>
        </>}>
        <div className="form-grid">
          <div className="form-group"><label>Account Code *</label><input value={form.account_code} onChange={e=>setForm({...form, account_code:e.target.value})}/></div>
          <div className="form-group"><label>Account Name *</label><input value={form.account_name} onChange={e=>setForm({...form, account_name:e.target.value})}/></div>
          <div className="form-group"><label>Type *</label>
            <select value={form.account_type} onChange={e=>setForm({...form, account_type:e.target.value})}>
              <option value="asset">Asset</option><option value="liability">Liability</option>
              <option value="equity">Equity</option><option value="income">Income</option><option value="expense">Expense</option>
            </select>
          </div>
          <div className="form-group"><label>Parent Account</label>
            <select value={form.parent_id||''} onChange={e=>setForm({...form, parent_id:e.target.value})}>
              <option value="">-- None --</option>
              {list.filter(a=>a.id!=editId).map(a=><option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Opening Balance</label><input type="number" value={form.opening_balance} onChange={e=>setForm({...form, opening_balance:e.target.value})}/></div>
        </div>
      </Modal>
    </div>
  );
}
